import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {IPageResponse, SortDirection} from "common/interfaces/IPageResponse";
import {IPageRequest} from "common/interfaces/IPageRequest";
import {
    GhgEmissionPeriod,
    GhgEmissionPeriodForm,
    GhgEmissionPeriodSortFields,
    isFormValid,
    translateServerError,
} from "components/GhgEmissionPeriods/models";
import {GhgEmissionPeriodsService} from "components/GhgEmissionPeriods/GhgEmissionPeriods.service";
import {GhgEmissionPeriodsSearchBar} from "components/GhgEmissionPeriods/GhgEmissionPeriodsSearchBar";
import {GhgEmissionPeriodsTable} from "components/GhgEmissionPeriods/GhgEmissionPeriodsTable";
import {
    DeleteGhgEmissionPeriodConfirmModal
} from "components/GhgEmissionPeriods/modals/DeleteGhgEmissionPeriodConfirmModal";

interface GhgEmissionPeriodsProps {
    onLoadValidatePage: () => void;
}

interface RowEditState {
    form: GhgEmissionPeriodForm;
    id?: number;
}

const GhgEmissionPeriods = (props: GhgEmissionPeriodsProps) => {
    const [periods, setPeriods] = useState<IPageResponse<GhgEmissionPeriod>>();
    const [pageRequest, setPageRequest] = useState<IPageRequest<GhgEmissionPeriodSortFields>>({
        page: 0,
        size: 20,
        sortDirection: SortDirection.DESC,
        sortField: GhgEmissionPeriodSortFields.PERIOD_START
    });
    const [isRequesting, setIsRequesting] = useState(false);
    const [rowEdit, setRowEdit] = useState<RowEditState>();
    const [validated, setValidated] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deletedPeriod, setDeletedPeriod] = useState<GhgEmissionPeriod>();

    const findPeriods = (request: any, showLoader: boolean) => {
        if (showLoader) setIsRequesting(true);

        GhgEmissionPeriodsService.findGhgEmissionPeriods(request)
            .then((response: IPageResponse<GhgEmissionPeriod>) => {
                setPeriods(response);
                setIsRequesting(false);
            })
            .catch(() => setIsRequesting(false));
    };

    const reload = (page: number, sortField: GhgEmissionPeriodSortFields, sortDirection: SortDirection, showLoader: boolean) => {
        findPeriods({
            page,
            size: pageRequest.size,
            sortField,
            sortDirection
        }, showLoader);
    };

    const closeRowEdit = () => {
        setRowEdit(undefined);
        setValidated(false);
    };

    const onPageChange = (targetPage: number) => {
        const page = targetPage - 1;
        closeRowEdit();
        setPageRequest({...pageRequest, page});
        reload(page, pageRequest.sortField, pageRequest.sortDirection, true);
    };

    const handleSortClick = (sortField: GhgEmissionPeriodSortFields) => {
        let sortDirection = SortDirection.ASC;
        if (pageRequest.sortField.match(sortField)) {
            sortDirection = pageRequest.sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC;
        }

        closeRowEdit();
        setPageRequest({...pageRequest, sortDirection, sortField});
        reload(pageRequest.page, sortField, sortDirection, false);
    };

    const handleStartCreate = () => {
        setValidated(false);
        setRowEdit({form: {
              periodStart: "",
              periodEnd: "",
              hvoRediesel: "",
              biomethane: ""
        }});
    };

    const handleStartEdit = (period: GhgEmissionPeriod) => {
        setValidated(false);
        setRowEdit({form: {
            periodStart: period.periodStart,
            periodEnd: period.periodEnd,
            hvoRediesel: String(period.hvoRediesel),
            biomethane: String(period.biomethane)
        }, id: period.id});
    };

    const handleFormChange = (change: Partial<GhgEmissionPeriodForm>) => {
        setRowEdit((current) => current && {
            ...current,
            form: {...current.form, ...change}
        });
    };

    const handleSave = async () => {
        if (!rowEdit) return;

        setValidated(true);

        const form = rowEdit.form;
        if (!isFormValid(form)) return;

        const isCreating = rowEdit.id === undefined;

        setSaving(true);

        try {
            const request = {
                periodStart: rowEdit.form.periodStart,
                periodEnd: form.periodEnd,
                hvoRediesel: Number(form.hvoRediesel),
                biomethane: Number(form.biomethane)
            };

            if (isCreating) {
                await GhgEmissionPeriodsService.postGhgEmissionPeriod(request);
            } else {
                await GhgEmissionPeriodsService.putGhgEmissionPeriod(rowEdit.id!, request);
            }

            closeRowEdit();

            reload(pageRequest.page, pageRequest.sortField, pageRequest.sortDirection, false);
            toast(isCreating ? "Perioodi loomine õnnestus" : "Perioodi uuendamine õnnestus", {
                position: "top-right",
                type: 'success'
            });
        } catch (e : any) {
            console.error('Saving failed', e);

            toast(translateServerError(e, isCreating
                ? "Perioodi loomine ebaõnnestus"
                : "Perioodi uuendamine ebaõnnestus"), {
                position: "top-right",
                type: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        props.onLoadValidatePage();
        reload(0, pageRequest.sortField, pageRequest.sortDirection, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="container">
            <div className="row">
                <div className="col">
                    <GhgEmissionPeriodsSearchBar
                        onCreate={handleStartCreate}
                        isCreateDisabled={rowEdit !== undefined || isRequesting}
                    />
                    <GhgEmissionPeriodsTable
                        periods={periods}
                        onPageChange={onPageChange}
                        onSort={handleSortClick}
                        sortField={pageRequest.sortField}
                        sortDirection={pageRequest.sortDirection}
                        isRequesting={isRequesting}
                        createForm={rowEdit && rowEdit.id === undefined ? rowEdit.form : undefined}
                        editForm={rowEdit && rowEdit.id !== undefined ? rowEdit.form : undefined}
                        editId={rowEdit?.id}
                        validated={validated}
                        saving={saving}
                        onFormChange={handleFormChange}
                        onSave={handleSave}
                        onCancel={closeRowEdit}
                        onStartEdit={handleStartEdit}
                        onDelete={setDeletedPeriod}
                    />
                </div>
            </div>
            {
                deletedPeriod && (
                    <DeleteGhgEmissionPeriodConfirmModal
                        period={deletedPeriod}
                        handleRejectClose={() => setDeletedPeriod(undefined)}
                        handleSubmitClose={() => {
                            setDeletedPeriod(undefined);
                            reload(pageRequest.page, pageRequest.sortField, pageRequest.sortDirection, false);
                        }}
                    />
                )
            }
        </div>
    );
}

export default GhgEmissionPeriods;
